import React, { useEffect, useState } from 'react';
import {
  isPushSupported,
  isStandalone,
  isIOS,
  getNotificationPermission,
  getExistingSubscription,
  subscribeToPush,
  unsubscribeFromPush,
} from '../../services/push';

const Icon = ({ children }) => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.7"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);

const ShareIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="np-inline-icon">
    <path d="M4 12C4 16.4183 7.58172 20 12 20C16.4183 20 20 16.4183 20 12" />
    <path d="M12 14L12 4M9 7L12 4L15 7" />
  </svg>
);

const NotificationsSettings = () => {
  const [status, setStatus] = useState('checking'); // checking | need-install | need-permission | denied | subscribed | unsupported
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const refresh = async () => {
    if (!isPushSupported()) {
      setStatus('unsupported');
      return;
    }
    if (isIOS() && !isStandalone()) {
      setStatus('need-install');
      return;
    }
    if (getNotificationPermission() === 'denied') {
      setStatus('denied');
      return;
    }
    const subscription = await getExistingSubscription();
    setStatus(subscription ? 'subscribed' : 'need-permission');
  };

  useEffect(() => { refresh(); }, []);

  const handleEnable = async () => {
    setBusy(true);
    setError('');
    try {
      await subscribeToPush();
      setStatus('subscribed');
    } catch (e) {
      setError(e?.message || 'Не удалось включить уведомления');
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    setBusy(true);
    setError('');
    try {
      await unsubscribeFromPush();
      setStatus('need-permission');
    } catch (e) {
      setError('Не удалось отключить уведомления');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="ui-card">
      <header className="ui-card-head">
        <h2>Уведомления на телефон</h2>
        <p>Push-уведомления о новых сообщениях и задачах приходят даже когда приложение закрыто.</p>
      </header>

      <div className="np-body">
        {status === 'checking' && <p className="ui-dim">Проверяем…</p>}

        {status === 'unsupported' && (
          <p className="ui-dim">Этот браузер не поддерживает push-уведомления.</p>
        )}

        {status === 'need-install' && (
          <div className="np-steps">
            <p>Чтобы получать уведомления на iPhone, сначала установите приложение на экран «Домой»:</p>
            <ol>
              <li>Откройте сайт в Safari (не в другом браузере)</li>
              <li>Нажмите на кнопку «Поделиться» <ShareIcon /> внизу экрана</li>
              <li>Выберите «На экран «Домой»» и нажмите «Добавить»</li>
              <li>Откройте приложение с экрана «Домой» и вернитесь на эту вкладку — здесь появится кнопка включения уведомлений</li>
            </ol>
          </div>
        )}

        {status === 'denied' && (
          <p className="ui-dim">
            Уведомления заблокированы в настройках. Разрешите их для этого сайта:
            {' '}iOS — Настройки → [название приложения] → Уведомления;
            {' '}на компьютере — иконка замка рядом с адресом сайта.
          </p>
        )}

        {status === 'need-permission' && (
          <button type="button" className="ui-btn-primary" onClick={handleEnable} disabled={busy}>
            <Icon>
              <path d="M18.7491 9.70957V9.00496C18.7491 5.13623 15.7274 2 12 2C8.27256 2 5.25087 5.13623 5.25087 9.00496V9.70957C5.25087 10.5552 5.00972 11.3818 4.5578 12.0854L3.45036 13.8095C2.43882 15.3843 3.21105 17.5249 4.97036 18.0229C9.57274 19.3257 14.4273 19.3257 19.0296 18.0229C20.789 17.5249 21.5612 15.3843 20.5496 13.8095L19.4422 12.0854C18.9903 11.3818 18.7491 10.5552 18.7491 9.70957Z" />
              <path d="M7.5 19C8.15503 20.7478 9.92246 22 12 22C14.0775 22 15.845 20.7478 16.5 19" />
            </Icon>
            {busy ? 'Включаем…' : 'Включить уведомления'}
          </button>
        )}

        {status === 'subscribed' && (
          <div className="np-active">
            <span className="ui-badge role-default">
              <Icon><path strokeLinecap="round" strokeLinejoin="round" d="M8.5 12.5L10.5 14.5L15.5 9.5" /></Icon>
              Уведомления включены
            </span>
            <button type="button" className="ui-btn-ghost" onClick={handleDisable} disabled={busy}>
              {busy ? 'Отключаем…' : 'Отключить'}
            </button>
          </div>
        )}

        {error && <p className="ui-hint-error">{error}</p>}
      </div>
    </section>
  );
};

export default NotificationsSettings;
